import React from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { IJsonSchema } from '@estee/elc-form';
import { FIELD_TYPE, ADDRESS_TYPE_ID, AU_POST_LOCATIONS_WIDGET_ID, DeliveryOptions } from '../../constants/Constants';
import { IAUPostCollections } from '../../domain/entities/Config';
import ConfigRepository from '../../repositories/ConfigRepository';
import ShippingRepository from '../../repositories/ShippingRepository';
import AddressRepository from '../../repositories/AddressRepository';
import AddressFormStore from '../../domain/entities/AddressFormStore';
import AUPostCollectionsRepository from '../../repositories/AUPostCollectionsStoreRepository';
import { formatValidationSchema, IJSONSchema } from '../../utils/AddressValidation';
import TransactionRepository from '../../repositories/TransactionRepository/index';
import { diContainer, serviceNames } from '../../diContainer/diContainer';
import { IAddressAttributes } from '../../domain/entities/Address';

export interface IAUPostCollecViewControllerConfig {
  addressRepository: ShippingRepository;
  configRepository: ConfigRepository;
  auPostCollectionsRepository: AUPostCollectionsRepository;
}

export interface IAUPostCollectionsData {
  address: IAUPostCollectionsAddress;
  formStore: AddressFormStore;
  isStoreSelected: boolean;
  widgetLoaded: boolean;
  widgetRef: React.RefObject<HTMLDivElement>;
  validationSchema: IJsonSchema;
}

export interface IAUPostCollectionsAddress {
  line1: string;
  line2: string;
  postcode: string;
  state: string;
  town: string;
}

export type FormStoreAUStoreMapping = {
  [FIELD_TYPE.CITY]: string;
  [FIELD_TYPE.FIRST_LINE_ADDRESS]: string;
  [FIELD_TYPE.SECOND_LINE_ADDRESS]: string;
  [FIELD_TYPE.STATE]: string;
  [FIELD_TYPE.ZIPCODE]: string;
};

@observer
class AUPostCollectionsViewController extends React.Component<IAUPostCollecViewControllerConfig> {
  private addressRepository: AddressRepository;

  private configRepository: ConfigRepository;

  private auPostCollectionsRepository: AUPostCollectionsRepository;

  @observable transactionRepository: TransactionRepository;

  @observable private isStoreSelected = false;

  @observable private loaded: boolean;

  @observable private widgetLoaded: boolean;

  @observable private address: IAUPostCollectionsAddress;

  @observable private widgetRef: React.RefObject<HTMLDivElement>;

  constructor(props: IAUPostCollecViewControllerConfig) {
    super(props);
    this.configRepository = props.configRepository;
    this.addressRepository = props.addressRepository;
    this.auPostCollectionsRepository = props.auPostCollectionsRepository;
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.loaded = false;
    this.widgetLoaded = false;
    this.widgetRef = React.createRef();
  }

  getConfig = (): IAUPostCollections | null => {
    const {
      transaction: {
        deliveryOptions,
      },
    } = this.transactionRepository;

    const config = deliveryOptions?.find(item => item.name === DeliveryOptions.AU_POST_COLLECTIONS);

    const {
      config: {
        AUPostCollections: auPostCollectionsConfig = null,
      } = {},
    } = config || {};

    return auPostCollectionsConfig;
  };

  @action
  setWidgetLoaded = (loaded: boolean): void => {
    this.widgetLoaded = loaded;
  };

  @action
  initializeWidget = (): void => {
    const config = this.getConfig();
    if (!config) {
      return;
    }

    if (!this.widgetLoaded) {
      const {
        apiKey,
        apiSecret,
        gooleMapApiKey,
        merchantId,
      } = config;

      // PG JSON API always wrap the string only contains number start from 0 with a wrapper
      // like 01234 -> [#--01234--#], so need to remove this wrapper before usage
      // https://github.com/elc-online/perlgem/blob/619eec2808e8a56ecee772914e846e7e26c6816c/server/generic/Generic/Rpc/Rpc.pm#L561
      const regexJsonSpecialChars = /(\[#--|--#\])/g;
      const merchantIdFormatted = merchantId.replace(regexJsonSpecialChars, '');

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      LocationFinder.init({
        apiKey,
        apiSecret,
        callback: this.updateFormStore,
        googleApiKey: gooleMapApiKey,
        merchantId: merchantIdFormatted,
        root: AU_POST_LOCATIONS_WIDGET_ID,
      });
      this.widgetLoaded = true;
    }

    if (!this.loaded) {
      const {
        transaction: {
          shippingAddress,
        },
      } = this.transactionRepository;
      const selected = shippingAddress?.attributes[FIELD_TYPE.ADDRESS_TYPE] === ADDRESS_TYPE_ID.PACKSTATION;
      this.isStoreSelected = selected;
      this.setLoaded(true);

      if (this.isStoreSelected) {
        const { formStore } = this.addressRepository;
        const {
          attributes: formValues,
        } = shippingAddress;
        formStore.setValues(formValues);
        Object.keys(formValues).forEach(key => {
          formStore.setFieldValue(key as keyof IAddressAttributes, formValues[key as keyof IAddressAttributes]);
        });
        this.auPostCollectionsRepository.isFormReady = true;
      }
    }
  };

  @action
  updateFormStore = (data: IAUPostCollectionsData): void => {
    const {
      addressRepository: {
        formStore,
      },
      configRepository: {
        config: {
          countryId,
        },
      },
    } = this;
    const { address } = data;

    const formStoreAUStoreMapping: FormStoreAUStoreMapping = {
      [FIELD_TYPE.CITY]: 'city',
      [FIELD_TYPE.FIRST_LINE_ADDRESS]: 'street',
      [FIELD_TYPE.SECOND_LINE_ADDRESS]: 'street2',
      [FIELD_TYPE.STATE]: 'state',
      [FIELD_TYPE.ZIPCODE]: 'postCode',
    };

    formStore.setFieldValue(FIELD_TYPE.COUNTRY_ID, countryId);
    formStore.setFieldValue(FIELD_TYPE.PHONE1_TYPE, 1);
    formStore.setFieldValue(FIELD_TYPE.ADDRESS_TYPE, ADDRESS_TYPE_ID.PACKSTATION);
    Object.keys(formStoreAUStoreMapping).forEach((key: keyof FormStoreAUStoreMapping) => {
      const fieldValue = address[formStoreAUStoreMapping[key] as keyof IAUPostCollectionsAddress];
      formStore.setFieldValue(key, fieldValue);
    });

    this.setIsStoreSelected(true);
    this.auPostCollectionsRepository.isFormReady = true;
    this.address = address;
  };

  @action
  setLoaded = (loaded: boolean): void => {
    this.loaded = loaded;
  };

  @action
  setIsStoreSelected = (isStoreSelected: boolean): void => {
    this.isStoreSelected = isStoreSelected;
  };

  @computed
  get validationSchema(): IJSONSchema {
    const {
      configRepository: {
        config: {
          contactInfoInputSchema,
          countryId,
        },
      },
    } = this;
    return formatValidationSchema(contactInfoInputSchema, countryId);
  }

  @computed
  get data(): IAUPostCollectionsData {
    return {
      address: this.address,
      formStore: this.addressRepository.formStore,
      isStoreSelected: this.isStoreSelected,
      validationSchema: this.validationSchema,
      widgetLoaded: this.widgetLoaded,
      widgetRef: this.widgetRef,
    };
  }
}

export default AUPostCollectionsViewController;
